/**
 * O formulário de disponibilidade, do lado da coordenação. P12.
 *
 * Substitui o questionário genérico que volta com 306 respostas para
 * redigitar e conferir (§14.4): aqui cada professor recebe um link dele, que
 * já diz o que ele precisa cobrir, e a resposta cai direto no cadastro. A
 * tela mostra quem tem link, quem respondeu e — o que interessa — **quem
 * respondeu sem cobrir**, com a explicação dele.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { ErroApi, escola, type Escola, formularios, type FormularioDoProfessor, type LinkDeFormulario } from '../api'
import { preencherConvite } from '../formulario'
import { type Registro, rotulosDistintos } from '../rotulos'

type Filtro = 'TODOS' | 'NAO_COBRE' | 'SEM_RESPOSTA' | 'SEM_LINK'

const ESTADO: Record<FormularioDoProfessor['estado'], string> = {
  SEM_LINK: 'sem link',
  ENVIADO: 'enviado',
  RESPONDIDO: 'respondido',
  VENCIDO: 'venceu sem resposta',
}

const dia = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : ''
const diaCompleto = (iso: string) => new Date(iso).toLocaleDateString('pt-BR')

export function Formularios({ projeto }: { projeto: string }) {
  const [lista, setLista] = useState<FormularioDoProfessor[]>([])
  const [tempos, setTempos] = useState(0)
  const [erro, setErro] = useState<string | null>(null)
  const [filtro, setFiltro] = useState<Filtro>('TODOS')
  const [escolhidos, setEscolhidos] = useState<Set<string>>(new Set())
  const [links, setLinks] = useState<LinkDeFormulario[] | null>(null)
  const [gerando, setGerando] = useState(false)
  const [copiado, setCopiado] = useState<string | null>(null)
  const [conviteP, setConviteP] = useState('')
  const [modoEmail, setModoEmail] = useState<{ modo: 'teste' | 'real' | 'bloqueado'; testes: string[] } | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)
  const [reabrindo, setReabrindo] = useState(false)
  // Os ajustes do formulário moram no projeto (0026). O rascunho é local até
  // "Salvar ajustes".
  const [config, setConfig] = useState<Escola | null>(null)
  const [rascunho, setRascunho] = useState({ creditos: 1, dias: 10, convite: '', coordenacao: '' })
  const [salvandoAjustes, setSalvandoAjustes] = useState(false)

  const carregar = useCallback(async () => {
    try {
      const [r, e] = await Promise.all([formularios.listar(projeto), escola.ler(projeto)])
      setLista(r.professores)
      setTempos(r.tempos)
      setConviteP(r.convite_padrao)
      setModoEmail(r.email)
      setConfig(e)
      setRascunho({
        creditos: e.creditos_por_tempo ?? 1,
        dias: e.dias_do_formulario ?? 10,
        convite: e.convite_formulario ?? '',
        coordenacao: (e.emails_coordenacao ?? []).join(', '),
      })
      setErro(null)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível carregar')
    }
  }, [projeto])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const rotulos = useMemo(() => rotulosDistintos(lista as unknown as Registro[]), [lista])

  const visiveis = lista.filter((p) =>
    filtro === 'NAO_COBRE'
      ? p.com_carga && p.faltas.length > 0
      : filtro === 'SEM_RESPOSTA'
        ? p.estado === 'ENVIADO' || p.estado === 'VENCIDO'
        : filtro === 'SEM_LINK'
          ? p.estado === 'SEM_LINK'
          : true,
  )
  const contar = (f: Filtro) =>
    lista.filter((p) =>
      f === 'NAO_COBRE'
        ? p.com_carga && p.faltas.length > 0
        : f === 'SEM_RESPOSTA'
          ? p.estado === 'ENVIADO' || p.estado === 'VENCIDO'
          : f === 'SEM_LINK'
            ? p.estado === 'SEM_LINK'
            : true,
    ).length

  const todosVisiveisEscolhidos = visiveis.length > 0 && visiveis.every((p) => escolhidos.has(p.id))

  const gerar = async () => {
    setGerando(true)
    try {
      const novos = await formularios.gerar(projeto, [...escolhidos])
      setLinks(novos)
      setCopiado(null)
      setAviso(null)
      setEscolhidos(new Set())
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível gerar os links')
    } finally {
      setGerando(false)
    }
  }

  /**
   * Reabre o mesmo link dos escolhidos, por mais `dias_do_formulario` dias.
   * É o passo "depois das inconsistências e das negociações" — o professor
   * corrige pelo link que já tem no e-mail.
   */
  const reabrir = async () => {
    setReabrindo(true)
    try {
      const r = await formularios.reabrir(projeto, [...escolhidos])
      const nomes = r.sem_link.map((id) => rotulos.get(id) ?? id)
      setAviso(
        (r.reabertos > 0
          ? `${r.reabertos} link(s) reaberto(s) até ${r.expira_em ? diaCompleto(r.expira_em) : ''}. Os professores usam o mesmo link que já receberam.`
          : 'Nenhum link reaberto.') +
          (nomes.length > 0 ? ` Sem link para reabrir — gere um novo: ${nomes.join(', ')}.` : ''),
      )
      setEscolhidos(new Set())
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível reabrir')
    } finally {
      setReabrindo(false)
    }
  }

  /** Gera os links e manda o convite por e-mail. A trava do modo de teste é do servidor. */
  const enviarPorEmail = async () => {
    setEnviando(true)
    try {
      const r = await formularios.enviar(projeto, [...escolhidos])
      setLinks(r.links)
      setCopiado(null)
      const nome = (id: string) => rotulos.get(id) ?? id
      const partes = [
        r.modo === 'bloqueado'
          ? 'Envio por e-mail desligado: nenhum e-mail saiu. Os links estão abaixo para mandar à mão.'
          : r.modo === 'teste'
            ? `${r.enviados} convite(s) enviado(s) em modo de teste — para ${r.para_teste.join(', ')}, não para os professores.`
            : `${r.enviados} convite(s) enviado(s) aos professores.`,
      ]
      if (r.sem_email.length > 0) partes.push(`Sem e-mail cadastrado: ${r.sem_email.map(nome).join(', ')}.`)
      if (r.falhas.length > 0 && r.modo !== 'bloqueado') {
        partes.push(`Falharam: ${r.falhas.map((f) => `${nome(f.professor_id)} (${f.erro})`).join('; ')}.`)
      }
      setAviso(partes.join(' '))
      setEscolhidos(new Set())
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível enviar')
    } finally {
      setEnviando(false)
    }
  }

  const salvarAjustes = async () => {
    setSalvandoAjustes(true)
    try {
      await escola.alterar(projeto, {
        creditos_por_tempo: rascunho.creditos,
        dias_do_formulario: rascunho.dias,
        convite_formulario: rascunho.convite.trim() || null,
        emails_coordenacao: rascunho.coordenacao,
      })
      setAviso('Ajustes do formulário salvos.')
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível salvar os ajustes')
    } finally {
      setSalvandoAjustes(false)
    }
  }

  const endereco = (token: string) => `${location.origin}/?formulario=${encodeURIComponent(token)}`

  const conviteDe = (l: LinkDeFormulario) =>
    preencherConvite(config?.convite_formulario, conviteP, {
      link: endereco(l.token),
      prazo: diaCompleto(l.expira_em),
      periodo: String(config?.ano_letivo ?? ''),
      professor: rotulos.get(l.professor_id) ?? l.nome,
    })
  const assunto = `Disponibilidade ${config?.ano_letivo ?? ''} — link individual`.replace(/\s+—/, ' —')

  // Uma linha por professor, separada por tabulação: cola direto numa
  // planilha de mala direta (nome, e-mail, link, prazo).
  const copiarTudo = async () => {
    if (!links) return
    const texto = links
      .map((l) =>
        [rotulos.get(l.professor_id) ?? l.nome, l.email ?? '', endereco(l.token), diaCompleto(l.expira_em)].join('\t'),
      )
      .join('\n')
    await navigator.clipboard.writeText(texto)
    setCopiado('todos')
  }

  const copiarConvite = async (l: LinkDeFormulario) => {
    await navigator.clipboard.writeText(conviteDe(l))
    setCopiado(l.professor_id)
  }

  return (
    <div className="section">
      <header className="page-head">
        <h1>Formulário de disponibilidade</h1>
        <p className="page-head__note">
          Um link para cada professor. O formulário diz quantas aulas ele precisa cobrir em cada turno e em que dia —{' '}
          <strong>sem dizer de que disciplina ou turma</strong>. A resposta entra direto na disponibilidade dele.
        </p>
      </header>

      {erro && (
        <p className="state state--pendencia" role="alert">
          {erro}
        </p>
      )}
      {tempos === 0 && lista.length > 0 && (
        <p className="callout">
          Ainda não há horários de aula nos esquemas. O formulário é feito deles — cadastre os esquemas antes de gerar
          os links.
        </p>
      )}

      {modoEmail && (
        <p className={modoEmail.modo === 'real' ? 'callout' : 'state state--pendencia'} role="note" data-tour="modo">
          {modoEmail.modo === 'teste' ? (
            <>
              <strong>Modo de teste.</strong> Todo e-mail vai para {modoEmail.testes.join(', ')} — nunca para os
              professores ou a coordenação, mesmo com e-mail cadastrado.
            </>
          ) : modoEmail.modo === 'bloqueado' ? (
            <>
              <strong>Envio por e-mail desligado.</strong> Nenhum e-mail sai até ser configurado. Os links e o convite
              continuam disponíveis para mandar à mão.
            </>
          ) : (
            <>
              <strong>Envio real ligado:</strong> os convites vão para o e-mail de cada professor.
            </>
          )}
        </p>
      )}

      {config && (
        <details className="section formularios__ajustes" data-tour="ajustes">
          <summary>
            <strong>Ajustes do formulário</strong>{' '}
            <span className="field__hint">
              quadradinho de {rascunho.creditos === 1 ? 'um horário' : `${rascunho.creditos} horários`} · prazo de{' '}
              {config.dias_do_formulario} dias · convite {config.convite_formulario ? 'da escola' : 'padrão'}
            </span>
          </summary>
          <div className="stack">
            <label className="field">
              <span className="field__label">Cada quadradinho é</span>
              <select
                className="input"
                value={rascunho.creditos}
                onChange={(e) => setRascunho({ ...rascunho, creditos: Number(e.target.value) })}
              >
                <option value={1}>um horário do esquema (a aula avulsa, de 50 min)</option>
                <option value={2}>dois horários seguidos — o tempo de aula (ex.: 1h40)</option>
                <option value={3}>três horários seguidos</option>
              </select>
              <span className="field__hint">
                O professor marca na mesma unidade em que a escola conta a aula. O pedido ("2 tempos à noite") é
                contado nela também.
              </span>
            </label>
            <label className="field">
              <span className="field__label">Prazo para responder, em dias</span>
              <input
                className="input"
                type="number"
                min={1}
                max={120}
                value={rascunho.dias}
                onChange={(e) => setRascunho({ ...rascunho, dias: Number(e.target.value) })}
              />
              <span className="field__hint">Vale para gerar e para reabrir.</span>
            </label>
            <label className="field">
              <span className="field__label">Texto do convite</span>
              <textarea
                className="input"
                rows={12}
                value={rascunho.convite}
                placeholder={conviteP}
                onChange={(e) => setRascunho({ ...rascunho, convite: e.target.value })}
              />
              <span className="field__hint">
                Em branco, vale o texto padrão (aparece apagado acima). Use <code>{'{link}'}</code> onde entra o
                endereço do formulário, <code>{'{prazo}'}</code> para a data-limite, <code>{'{periodo}'}</code> para o
                ano e <code>{'{professor}'}</code> para o nome.
              </span>
            </label>
            <label className="field">
              <span className="field__label">Quem recebe os alertas</span>
              <input
                className="input"
                value={rascunho.coordenacao}
                placeholder="horario@escola.br, coordenacao@escola.br"
                onChange={(e) => setRascunho({ ...rascunho, coordenacao: e.target.value })}
              />
              <span className="field__hint">
                E-mails separados por vírgula. Recebem o aviso quando um professor deixa o link vencer sem responder ou
                responde sem cobrir a carga. Em branco, o aviso fica só em Pendências.
              </span>
            </label>
            <div className="row">
              <button
                type="button"
                className="btn btn--primary"
                disabled={salvandoAjustes}
                onClick={() => void salvarAjustes()}
              >
                {salvandoAjustes ? 'Salvando…' : 'Salvar ajustes'}
              </button>
            </div>
          </div>
        </details>
      )}

      {links && (
        <section className="section stack" data-tour="links">
          <div className="section__head">
            <h2>Links gerados agora</h2>
            <p className="field__hint">
              Copie agora: por segurança, o sistema guarda só uma impressão do link e não consegue mostrá-lo de novo.
              Gerar outro para o mesmo professor invalida este. O convite já vem com o link e o prazo de cada um;
              "Abrir no e-mail" monta a mensagem no seu programa de e-mail.
            </p>
          </div>
          <div className="row">
            <button type="button" className="btn btn--primary" onClick={() => void copiarTudo()}>
              Copiar todos ({links.length})
            </button>
            {copiado === 'todos' && (
              <span className="state state--ok" role="status">
                Copiado: nome, e-mail, link e prazo, uma linha por professor — para a mala direta.
              </span>
            )}
            <button type="button" className="btn btn--ghost" onClick={() => setLinks(null)}>
              Fechar
            </button>
          </div>
          <div className="tabela-rolavel">
            <table className="tabela">
              <tbody>
                {links.map((l) => (
                  <tr key={l.professor_id}>
                    <td>{rotulos.get(l.professor_id) ?? l.nome}</td>
                    <td>{l.email ?? <span className="field__hint">sem e-mail</span>}</td>
                    <td className="formularios__links">{endereco(l.token)}</td>
                    <td>
                      <div className="row">
                        <button type="button" className="btn btn--ghost" onClick={() => void copiarConvite(l)}>
                          {copiado === l.professor_id ? 'Convite copiado' : 'Copiar convite'}
                        </button>
                        {l.email && (
                          <a
                            className="btn btn--ghost"
                            href={`mailto:${encodeURIComponent(l.email)}?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(conviteDe(l))}`}
                          >
                            Abrir no e-mail
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="row">
        <div className="segmented segmented--texto" role="group" aria-label="Filtrar professores" data-tour="filtros">
          {(
            [
              ['TODOS', 'Todos'],
              ['NAO_COBRE', 'Não cobrem a carga'],
              ['SEM_RESPOSTA', 'Sem resposta'],
              ['SEM_LINK', 'Sem link'],
            ] as const
          ).map(([id, rotulo]) => (
            <button
              key={id}
              type="button"
              className="segmented__option"
              aria-pressed={filtro === id}
              onClick={() => setFiltro(id)}
            >
              {rotulo} <span className="mono">{contar(id)}</span>
            </button>
          ))}
        </div>
        <button
          type="button"
          className="btn btn--secondary"
          data-tour="gerar"
          disabled={escolhidos.size === 0 || gerando || tempos === 0}
          onClick={() => void gerar()}
        >
          {gerando ? 'Gerando…' : `Gerar link para ${escolhidos.size} professor(es)`}
        </button>
        <button
          type="button"
          className="btn btn--primary"
          data-tour="enviar"
          title="Gera o link de cada um e manda o convite por e-mail"
          disabled={escolhidos.size === 0 || enviando || tempos === 0 || modoEmail?.modo === 'bloqueado'}
          onClick={() => void enviarPorEmail()}
        >
          {enviando
            ? 'Enviando…'
            : `Enviar convite por e-mail${modoEmail?.modo === 'teste' ? ' (teste)' : ''}`}
        </button>
        <button
          type="button"
          className="btn btn--secondary"
          title="Estende o prazo do link que o professor já tem, sem gerar outro"
          disabled={escolhidos.size === 0 || reabrindo}
          onClick={() => void reabrir()}
        >
          {reabrindo ? 'Reabrindo…' : 'Reabrir para ajuste'}
        </button>
      </div>
      {aviso && (
        <p className="state state--ok" role="status">
          {aviso}
        </p>
      )}

      <div className="tabela-rolavel">
        <table className="tabela" data-tour="lista">
          <thead>
            <tr>
              <th scope="col">
                <input
                  type="checkbox"
                  aria-label="Escolher todos os da lista"
                  checked={todosVisiveisEscolhidos}
                  onChange={() =>
                    setEscolhidos((atual) => {
                      const novo = new Set(atual)
                      for (const p of visiveis) {
                        if (todosVisiveisEscolhidos) novo.delete(p.id)
                        else novo.add(p.id)
                      }
                      return novo
                    })
                  }
                />
              </th>
              <th scope="col">Professor</th>
              <th scope="col">O que o formulário pede</th>
              <th scope="col">Formulário</th>
              <th scope="col">Disponibilidade de hoje</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((p) => (
              <tr key={p.id}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Escolher ${rotulos.get(p.id) ?? p.nome}`}
                    checked={escolhidos.has(p.id)}
                    onChange={() =>
                      setEscolhidos((atual) => {
                        const novo = new Set(atual)
                        if (novo.has(p.id)) novo.delete(p.id)
                        else novo.add(p.id)
                        return novo
                      })
                    }
                  />
                </td>
                <td>
                  {rotulos.get(p.id) ?? p.nome}
                  {!p.email && <div className="field__hint">sem e-mail</div>}
                </td>
                <td>
                  {p.com_carga ? (
                    p.pedido.join('; ')
                  ) : (
                    <span className="field__hint">sem carga — recebe o formulário aberto, sem números</span>
                  )}
                </td>
                <td>
                  {ESTADO[p.estado]}
                  {p.estado === 'ENVIADO' && <div className="field__hint">vale até {dia(p.expira_em)}</div>}
                  {p.estado === 'RESPONDIDO' && <div className="field__hint">em {dia(p.respondido_em)}</div>}
                  {p.convite_enviado_em && (
                    <div className="field__hint">convite por e-mail em {dia(p.convite_enviado_em)}</div>
                  )}
                  {p.estado !== 'SEM_LINK' && (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={async () => {
                        await formularios.revogar(projeto, p.id)
                        await carregar()
                      }}
                    >
                      Invalidar link
                    </button>
                  )}
                </td>
                <td>
                  {!p.com_carga ? (
                    ''
                  ) : p.faltas.length === 0 ? (
                    <span className="state state--ok">cobre</span>
                  ) : (
                    <>
                      <ul className="state state--pendencia">
                        {p.faltas.map((f) => (
                          <li key={f}>{f}</li>
                        ))}
                      </ul>
                      {p.justificativa && <p className="field__hint">Explicação: “{p.justificativa}”</p>}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
